import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { z } from 'zod';

const PasswordSchema = z.object({
  password: z.string().min(6, 'Password must be at least 6 characters'),
  confirmPassword: z.string().min(6, 'Password must be at least 6 characters')
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"]
});

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isValidToken, setIsValidToken] = useState(false);
  const [isCheckingToken, setIsCheckingToken] = useState(true);

  useEffect(() => {
    let mounted = true;
    let timeoutId: NodeJS.Timeout | null = null;

    const checkUrlForToken = () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const queryParams = new URLSearchParams(window.location.search);

      const accessToken = hashParams.get('access_token') || queryParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token') || queryParams.get('refresh_token');
      const type = hashParams.get('type') || queryParams.get('type');
      const errorCode = hashParams.get('error_code') || queryParams.get('error_code');
      const errorDescription = hashParams.get('error_description') || queryParams.get('error_description');

      console.log('🔍 ResetPassword: URL parameters:', {
        hasAccessToken: !!accessToken,
        hasRefreshToken: !!refreshToken,
        type,
        errorCode,
        errorDescription,
        hash: window.location.hash,
        search: window.location.search
      });

      if (errorCode || errorDescription) {
        console.error('❌ ResetPassword: Error in URL:', { errorCode, errorDescription });
        return {
          hasError: true,
          errorMessage: errorDescription || 'Password reset link is invalid or has expired.'
        };
      }

      return {
        hasError: false,
        hasToken: !!(accessToken && type === 'recovery'),
        accessToken,
        refreshToken,
        type
      };
    };

    const checkSession = async () => {
      try {
        console.log('🔍 ResetPassword: Checking for recovery session...');

        const urlCheck = checkUrlForToken();

        if (urlCheck.hasError) {
          if (!mounted) return;
          setError(urlCheck.errorMessage || 'Invalid or expired reset link. Please request a new password reset.');
          setIsValidToken(false);
          setIsCheckingToken(false);
          return;
        }

        if (urlCheck.hasToken) {
          console.log('✅ ResetPassword: Valid recovery token found in URL');
          if (!mounted) return;
          setIsValidToken(true);
          setIsCheckingToken(false);
          setError('');
          return;
        }

        const { data: { session }, error } = await supabase.auth.getSession();

        console.log('🔍 ResetPassword: Session check result:', {
          hasSession: !!session,
          error: error?.message,
          sessionUser: session?.user?.id
        });

        if (!mounted) return;

        if (error || !session) {
          console.error('❌ ResetPassword: No valid session found:', error);
          setError('Invalid or expired reset link. Please request a new password reset.');
          setIsValidToken(false);
          setIsCheckingToken(false);
        } else {
          console.log('✅ ResetPassword: Valid recovery session found');
          setIsValidToken(true);
          setIsCheckingToken(false);
        }
      } catch (err) {
        console.error('❌ ResetPassword: Error checking session:', err);
        if (!mounted) return;
        setError('Failed to verify reset link. Please try again.');
        setIsValidToken(false);
        setIsCheckingToken(false);
      }
    };

    const handleAuthStateChange = supabase.auth.onAuthStateChange((event, session) => {
      console.log('🔍 ResetPassword: Auth state changed:', event, session?.user?.id);

      if (!mounted) return;

      if (event === 'PASSWORD_RECOVERY') {
        console.log('✅ ResetPassword: Password recovery event detected');
        setIsValidToken(true);
        setIsCheckingToken(false);
        setError('');
      } else if (event === 'SIGNED_IN' && session) {
        console.log('✅ ResetPassword: User signed in with recovery token');
        setIsValidToken(true);
        setIsCheckingToken(false);
        setError('');
      }
    });

    timeoutId = setTimeout(() => {
      if (mounted) {
        checkSession();
      }
    }, 500);

    return () => {
      mounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      handleAuthStateChange.data.subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    try {
      const validatedData = PasswordSchema.parse({ password, confirmPassword });
      setIsSubmitting(true);

      const { error } = await supabase.auth.updateUser({
        password: validatedData.password
      });

      if (error) throw error;

      setSuccess(true);

      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      if (err instanceof z.ZodError) {
        setError(err.errors[0].message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to reset password. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isCheckingToken) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-gray-400">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  if (!isValidToken) {
    return (
      <div className="min-h-screen bg-black flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <img src="/Final logo file-04.svg" alt="Spectacle" className="h-16 w-16" />
          </div>
          <div className="mt-8 backdrop-blur-md bg-[#272727] py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded-lg">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                <span>{error}</span>
              </div>
            </div>
            <div className="mt-6 text-center">
              <a
                href="/forgot-password"
                className="text-sm font-medium text-purple-500 hover:text-purple-400 transition-colors"
              >
                Request a new reset link
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img src="/Final logo file-04.svg" alt="Spectacle" className="h-16 w-16" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
          Set new password
        </h2>
        <p className="mt-2 text-center text-sm text-gray-400">
          Enter your new password below
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="backdrop-blur-md bg-[#272727] py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {success && (
            <div className="mb-6 bg-green-900/50 border border-green-700 text-green-200 px-4 py-3 rounded-lg">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                <span>Password reset successful! Redirecting to login...</span>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-6 bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded-lg">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                <span>{error}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                New Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 bg-[#272727] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 border border-gray-700 pr-10 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Enter new password"
                  required
                  autoComplete="new-password"
                  maxLength={100}
                  disabled={isSubmitting || success}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  disabled={isSubmitting || success}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-1">
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-2 bg-[#272727] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 border border-gray-700 pr-10 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Confirm new password"
                  required
                  autoComplete="new-password"
                  maxLength={100}
                  disabled={isSubmitting || success}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  disabled={isSubmitting || success}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting || success || !password.trim() || !confirmPassword.trim()}
              className="w-full py-2 px-4 bg-purple-500 text-white rounded-lg hover:bg-purple-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2" />
                  Resetting Password...
                </>
              ) : success ? (
                'Password Reset!'
              ) : (
                'Reset Password'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
